import { requestTwoFactor } from '../security/twoFactorChallenge';
import axios from 'axios';
import { safeGetItem, safeRemoveItem } from '../utils/safeStorage';
import {
  decryptStorefrontTransportResponse,
  isStorefrontTransportTokenError,
  prepareStorefrontTransportRequest,
  resetStorefrontTransportSession,
  restoreStorefrontPlainRequest,
} from './storefrontTransport';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const DEFAULT_TIMEOUT_MS = 12 * 1000;
const DEFAULT_GET_RETRY_COUNT = 1;
const DEFAULT_GET_RETRY_DELAY_MS = 600;
const DEFAULT_TTL_MS = 30 * 1000;
const CACHE_RULES = [
  { match: '/store/home', ttlMs: 60 * 1000 },
  { match: '/store/games', ttlMs: 60 * 1000 },
  { match: '/store/items', ttlMs: 60 * 1000 },
  { match: '/store/banners', ttlMs: 60 * 1000 },
  { match: '/store/events', ttlMs: 60 * 1000 },
  { match: '/auth/me', ttlMs: 15 * 1000 },
  { match: '/auth/wallet-history', ttlMs: 15 * 1000 },
  { match: '/auth/transactions', ttlMs: 15 * 1000 },
];

const responseCache = new Map();
const inflightRequests = new Map();
let cacheGeneration = 0;

export const isRequestCanceled = (error) =>
  axios.isCancel(error)
  || error?.code === 'ERR_CANCELED'
  || error?.config?.signal?.aborted;

const isRetryableGetError = (error, config = {}) => {
  if (isRequestCanceled(error) || config.signal?.aborted) {
    return false;
  }

  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get') {
    return false;
  }

  const status = Number(error?.response?.status || 0);
  if (!status) {
    return true;
  }

  return status === 408 || status === 425 || status === 429 || status >= 500;
};

const waitForRetry = (delayMs, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new axios.CanceledError('Request canceled'));
      return;
    }

    let timeoutId;

    const handleAbort = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', handleAbort);
      reject(new axios.CanceledError('Request canceled'));
    };

    timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    }, delayMs);

    signal?.addEventListener('abort', handleAbort, { once: true });
  });

const stableSerialize = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${key}:${stableSerialize(value[key])}`)
      .join(',')}}`;
  }

  return String(value ?? '');
};

const cloneResponse = (response) => ({
  ...response,
  data:
    response?.data && typeof response.data === 'object'
      ? JSON.parse(JSON.stringify(response.data))
      : response?.data,
  headers: { ...(response?.headers || {}) },
  config: { ...(response?.config || {}) },
});

const getTokenFingerprint = () => {
  const token = safeGetItem('accessToken', '') || '';
  return token ? token.slice(-16) : 'guest';
};

const getCacheKey = (config = {}) =>
  [
    API_BASE_URL,
    config.url || '',
    stableSerialize(config.params || {}),
    getTokenFingerprint(),
  ].join('|');

const getCacheTtl = (config = {}) => {
  if (typeof config.cacheTtlMs === 'number') {
    return config.cacheTtlMs;
  }

  const matchedRule = CACHE_RULES.find((rule) => (config.url || '').includes(rule.match));
  return matchedRule?.ttlMs || DEFAULT_TTL_MS;
};

const isCacheableGet = (config = {}) => {
  const method = (config.method || 'get').toLowerCase();
  const responseType = config.responseType || 'json';

  return method === 'get' && config.cache !== false && (responseType === 'json' || responseType === '');
};

const readFromCache = (cacheKey) => {
  const cached = responseCache.get(cacheKey);
  if (!cached) return null;

  if (cached.expiresAt <= Date.now()) {
    responseCache.delete(cacheKey);
    return null;
  }

  return cloneResponse(cached.response);
};

export const clearApiCache = (matcher) => {
  cacheGeneration += 1;
  if (!matcher) {
    responseCache.clear();
    inflightRequests.clear();
    return;
  }

  for (const key of inflightRequests.keys()) {
    if (typeof matcher === 'function' ? matcher(key) : key.includes(matcher)) {
      inflightRequests.delete(key);
    }
  }
  for (const key of responseCache.keys()) {
    if (typeof matcher === 'function' ? matcher(key) : key.includes(matcher)) {
      responseCache.delete(key);
    }
  }
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

const rawRequest = api.request.bind(api);

const requestWithGetRetry = async (config = {}) => {
  const configuredRetryCount = Number(config.getRetryCount);
  const retryCount = Number.isFinite(configuredRetryCount)
    ? Math.max(0, configuredRetryCount)
    : DEFAULT_GET_RETRY_COUNT;
  const configuredRetryDelay = Number(config.getRetryDelayMs);
  const retryDelayMs = Number.isFinite(configuredRetryDelay)
    ? Math.max(0, configuredRetryDelay)
    : DEFAULT_GET_RETRY_DELAY_MS;

  let attempt = 0;

  while (true) {
    try {
      return await rawRequest(config);
    } catch (error) {
      if (attempt >= retryCount || !isRetryableGetError(error, config)) {
        throw error;
      }

      attempt += 1;
      await waitForRetry(retryDelayMs * attempt, config.signal);
    }
  }
};

api.request = async function requestWithCache(configOrUrl, maybeConfig) {
  const config =
    typeof configOrUrl === 'string'
      ? { ...(maybeConfig || {}), url: configOrUrl }
      : { ...(configOrUrl || {}) };

  if (!isCacheableGet(config)) {
    return (config.method || 'get').toLowerCase() === 'get'
      ? requestWithGetRetry(config)
      : rawRequest(config);
  }

  const cacheKey = getCacheKey(config);
  const canDedupeRequest = !config.signal;
  const cachedResponse = readFromCache(cacheKey);
  if (cachedResponse) {
    return cachedResponse;
  }

  if (canDedupeRequest && inflightRequests.has(cacheKey)) {
    return inflightRequests.get(cacheKey).then(cloneResponse);
  }

  const generation = cacheGeneration;
  const requestPromise = requestWithGetRetry(config)
    .then((response) => {
      if (generation === cacheGeneration) responseCache.set(cacheKey, {
        response: cloneResponse(response),
        expiresAt: Date.now() + getCacheTtl(config),
      });

      return cloneResponse(response);
    })
    .finally(() => {
      if (canDedupeRequest && inflightRequests.get(cacheKey) === requestPromise) {
        inflightRequests.delete(cacheKey);
      }
    });

  if (canDedupeRequest) {
    inflightRequests.set(cacheKey, requestPromise);
  }
  return requestPromise;
};

api.interceptors.request.use(
  async (config) => {
    const token = safeGetItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return prepareStorefrontTransportRequest(config);
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  async (response) => {
    await decryptStorefrontTransportResponse(response);

    if (response.data?.status === 'two_factor_required') {
      const data = await requestTwoFactor(response.data.data);
      return { ...response, data };
    }

    const method = (response.config?.method || 'get').toLowerCase();
    if (method !== 'get' && !response.config?.skipCacheInvalidation) {
      clearApiCache();
    }

    return response;
  },
  async (error) => {
    if (error.response) {
      await decryptStorefrontTransportResponse(error.response);
    }

    if (
      isStorefrontTransportTokenError(error.response)
      && !error.config?._storefrontTransportRetried
    ) {
      resetStorefrontTransportSession();
      const retryConfig = restoreStorefrontPlainRequest({
        ...error.config,
        _storefrontTransportRetried: true,
      });
      return api.request(retryConfig);
    }

    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const method = (error.config?.method || 'get').toLowerCase();

      const isLoginRequest = requestUrl.includes('/auth/login');
      const isTelegramMiniAppLogin = requestUrl.includes('/auth/telegram-miniapp');
      const isUpdateProfile = requestUrl.includes('/users/') && method === 'put';

      if (!isLoginRequest && !isTelegramMiniAppLogin && !isUpdateProfile) {
        safeRemoveItem('accessToken');
        safeRemoveItem('user');
        clearApiCache();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
